import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { detailPending, detailSettled } from "helpers/detailRequest";
import apiAxios from "helpers/apiAxios";
import { handleAsyncThunkError, addRejectedMatcher } from "../reduxHelpers";

const BASE = "/erp/purchase_orders";

export const getPurchaseOrders = createAsyncThunk(
  "PurchaseOrder/getPurchaseOrders",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(BASE, { params });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to load purchase orders."));
    }
  }
);

export const getPurchaseOrderInbox = createAsyncThunk(
  "PurchaseOrder/getPurchaseOrderInbox",
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/inbox`, { params });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to load approval inbox."));
    }
  }
);

export const getPurchaseOrder = createAsyncThunk(
  "PurchaseOrder/getPurchaseOrder",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/${id}`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to load purchase order."));
    }
  }
);

// PO luôn tạo từ 1 Purchase Requisition đã approved — data phải kèm purchase_request_id.
export const createPurchaseOrder = createAsyncThunk(
  "PurchaseOrder/createPurchaseOrder",
  async (data, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(BASE, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to create purchase order."));
    }
  }
);

export const updatePurchaseOrder = createAsyncThunk(
  "PurchaseOrder/updatePurchaseOrder",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.put(`${BASE}/${id}`, data);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to update purchase order."));
    }
  }
);

export const deletePurchaseOrder = createAsyncThunk(
  "PurchaseOrder/deletePurchaseOrder",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.delete(`${BASE}/${id}`);
      return { id, ...res.data };
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to delete purchase order."));
    }
  }
);

export const submitPurchaseOrder = createAsyncThunk(
  "PurchaseOrder/submitPurchaseOrder",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/submit`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to submit for approval."));
    }
  }
);

export const withdrawPurchaseOrder = createAsyncThunk(
  "PurchaseOrder/withdrawPurchaseOrder",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/withdraw`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Failed to withdraw the order."));
    }
  }
);

export const approvePurchaseOrder = createAsyncThunk(
  "PurchaseOrder/approvePurchaseOrder",
  async ({ id, comment }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/approve`, { comment });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Approval failed."));
    }
  }
);

export const rejectPurchaseOrder = createAsyncThunk(
  "PurchaseOrder/rejectPurchaseOrder",
  async ({ id, comment }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/reject`, { comment });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Rejection failed."));
    }
  }
);

// Cancel — người lập tự huỷ hẳn phiếu (draft/pending), khác Withdraw (rút về draft để sửa).
export const cancelPurchaseOrder = createAsyncThunk(
  "PurchaseOrder/cancelPurchaseOrder",
  async ({ id, comment }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/cancel`, { comment });
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Cancel failed."));
    }
  }
);

// Duplicate — tạo 1 PO mới ở Draft, copy nội dung từ phiếu này.
export const duplicatePurchaseOrder = createAsyncThunk(
  "PurchaseOrder/duplicatePurchaseOrder",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/duplicate`);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Duplicate failed."));
    }
  }
);

export const uploadPurchaseOrderDocument = createAsyncThunk(
  "PurchaseOrder/uploadPurchaseOrderDocument",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await apiAxios.post(`${BASE}/${id}/upload_document`, formData);
      return res.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, "Document upload failed."));
    }
  }
);

// Nguồn chọn PR (đã approved) khi tạo PO mới.
export const searchApprovedPurchaseRequests = async () => {
  const res = await apiAxios.get(`${BASE}/approved_purchase_requests`);
  return res.data;
};

// Bản in: backend sinh PDF theo mẫu QF-PUR-03 (có chữ ký), mở tab mới để xem/in.
// timeout dài hơn mặc định (30s) vì server phải chạy wkhtmltopdf + tải ảnh chữ ký
// từng người ký từ SignHub — có thể mất hơn 30s khi PO có nhiều bước ký hoặc SignHub chậm.
export const openPurchaseOrderPdf = async (id) => {
  const res = await apiAxios.get(`${BASE}/${id}/print`, { responseType: "blob", timeout: 120000 });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type || "application/pdf" }));
  const win = window.open(url, "_blank");
  if (!win) {
    const cd = res.headers?.["content-disposition"] || "";
    const match = cd.match(/filename="?([^"]+)"?/);
    const a = document.createElement("a");
    a.href = url;
    a.download = match ? match[1] : `purchase-order-${id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

// Excel: fill mẫu .xls tĩnh (app/templates/erp-pr-po/purchase_order.xls) rồi tải về.
export const downloadPurchaseOrderExcel = async (id) => {
  const res = await apiAxios.get(`${BASE}/${id}/print_excel`, { responseType: "blob", timeout: 120000 });
  const cd = res.headers?.["content-disposition"] || "";
  const match = cd.match(/filename="?([^"]+)"?/);
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.ms-excel" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = match ? match[1] : `purchase-order-${id}.xls`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

const initialState = {
  allData: { rows: [], total_rows: 0 },
  inboxData: { rows: [], total_rows: 0 },
  recordData: {},
  loading: false,
  error: null,
};

const purchaseOrderSlice = createSlice({
  name: "PurchaseOrder",
  initialState,
  reducers: {
    clearPurchaseOrder: (state) => {
      state.recordData = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPurchaseOrders.pending, (state) => { state.loading = true; })
      .addCase(getPurchaseOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.allData = action.payload;
      })
      .addCase(getPurchaseOrderInbox.fulfilled, (state, action) => {
        state.inboxData = action.payload;
      })
      .addCase(getPurchaseOrder.pending, (state, action) => {
        state.loading = true;
        detailPending()(state, action);
      })
      .addCase(getPurchaseOrder.fulfilled, detailSettled((state, action) => {
        state.loading = false;
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      }))
      .addCase(getPurchaseOrder.rejected, detailSettled())
      .addCase(createPurchaseOrder.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(updatePurchaseOrder.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(submitPurchaseOrder.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(withdrawPurchaseOrder.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(approvePurchaseOrder.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(rejectPurchaseOrder.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      })
      .addCase(cancelPurchaseOrder.fulfilled, (state, action) => {
        state.recordData = action.payload?.data?.attributes || action.payload?.data || {};
      });

    addRejectedMatcher(builder, "PurchaseOrder");
  },
});

export const { clearPurchaseOrder } = purchaseOrderSlice.actions;
export default purchaseOrderSlice.reducer;
